import { Configuration } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { SentryTeams } from '@mews/enums';
import {
    createSentryMiddleware,
    createSentryReporterService,
    FeatureFlagService,
    getSentryTeamConfig,
    SENTRY_ERROR_FILTERS,
    setSentryTransport,
} from '@mews/framework';

type CreateSentryParams = Pick<
    Configuration['configuration'],
    'sentryConfigurations' | 'environment' | 'platformUrls' | 'assetBaseUrl'
>;

export const createSentryServices = (
    { sentryConfigurations, environment, platformUrls, assetBaseUrl }: CreateSentryParams,
    featureFlagService: FeatureFlagService
) => {
    const commonConfig = { environment, release: `mews-commander@${__VERSION__}` };
    const teamConfigs = sentryConfigurations.map((config) => ({ ...commonConfig, ...config }));

    const reservationsSentryDsn = getSentryTeamConfig(teamConfigs, SentryTeams.Reservations)?.dsn;
    const productivitySentryDsn = getSentryTeamConfig(teamConfigs, SentryTeams.Productivity)?.dsn;
    const adminSentryDsn = getSentryTeamConfig(teamConfigs, SentryTeams.Admin)?.dsn;
    const connectivitySentryDsn = getSentryTeamConfig(teamConfigs, SentryTeams.Connectivity)?.dsn;
    const guestPortalSentryDsn = getSentryTeamConfig(teamConfigs, SentryTeams.GuestPortal)?.dsn;
    const accountingSentryDsn = getSentryTeamConfig(teamConfigs, SentryTeams.Accounting)?.dsn;
    const usersSentryDsn = getSentryTeamConfig(teamConfigs, SentryTeams.Users)?.dsn;

    let connectivitySentryService;
    let guestPortalSentryService;
    let reservationsSentryService;
    let productivitySentryService;
    let accountingSentryService;
    let usersSentryService;
    let adminSentryService;
    let sentryMiddleware;

    if (!__LOCALHOST__ && reservationsSentryDsn) {
        reservationsSentryService = createSentryReporterService({ ...commonConfig, dsn: reservationsSentryDsn });
        productivitySentryService = productivitySentryDsn
            ? createSentryReporterService({ ...commonConfig, dsn: productivitySentryDsn })
            : undefined;
        adminSentryService = adminSentryDsn
            ? createSentryReporterService({ ...commonConfig, dsn: adminSentryDsn })
            : undefined;
        connectivitySentryService = connectivitySentryDsn
            ? createSentryReporterService({ ...commonConfig, dsn: connectivitySentryDsn })
            : undefined;
        guestPortalSentryService = guestPortalSentryDsn
            ? createSentryReporterService({ ...commonConfig, dsn: guestPortalSentryDsn })
            : undefined;
        accountingSentryService = accountingSentryDsn
            ? createSentryReporterService({ ...commonConfig, dsn: accountingSentryDsn })
            : undefined;
        usersSentryService = usersSentryDsn
            ? createSentryReporterService({ ...commonConfig, dsn: usersSentryDsn })
            : undefined;

        sentryMiddleware = createSentryMiddleware({
            ...commonConfig,
            dsn: reservationsSentryDsn,
            shouldRecordErrorSessions: false,
            sentryReporterService: reservationsSentryService,
            allowUrls: [...platformUrls, assetBaseUrl],
            denyUrls: [
                // Chrome extensions
                /extensions\//i,
                /^chrome:\/\//i,
            ],
            ignoreErrors: [
                /NotAuthorizedError/,
                /NotAllowedError/,
                /BusinessError/,
                /BadRequestError/,
                /QuotaExceededError/,
                ...SENTRY_ERROR_FILTERS,
            ],
            transport: featureFlagService.hasFlag(FeatureFlag.sentryErrorGroupingEnabled)
                ? setSentryTransport(teamConfigs)
                : undefined,
            tracesSampler: () => (featureFlagService.hasFlag(FeatureFlag.sentryTracingEnabled) ? 1 : 0),
        });
    }

    return {
        adminSentryService,
        connectivitySentryService,
        guestPortalSentryService,
        productivitySentryService,
        reservationsSentryService,
        accountingSentryService,
        usersSentryService,
        sentryMiddleware,
    };
};
